/**
 * RouteRoleGate — shell-level RBAC enforcement for protected routes.
 *
 * Runs INSIDE the React tree, unlike a TanStack Router `beforeLoad` guard
 * (which executes outside React). This placement is deliberate and fixes a
 * cold-load deadlock:
 *
 *   Role state lives in RoleContext, which only exists once `MainAppLayout`
 *   has rendered. `MainAppLayout` registers the role on every render. A
 *   `beforeLoad` guard that awaited that registration deadlocked on hard
 *   refresh / direct deep-link: on the initial load TanStack awaits the
 *   matched `beforeLoad` chain BEFORE committing and rendering the root, so
 *   `MainAppLayout` could not mount, the role never registered, the guard
 *   timed out, and every protected deep-link was bounced to `/dashboard`.
 *   SPA navigation hid the bug because the layout was already mounted.
 *
 * Enforcing here — after the role query resolves — preserves the deep-link
 * and still redirects unauthorized users.
 *
 * Use `decideScreen` / `canAccessAdminArea` (which grant platform owners
 * every screen). Do NOT switch to `getVisibleScreens()`: its owner array
 * omits 'review-queue' and would silently lock owners out — a regression we
 * already shipped once (pinned in route-permissions.test.ts).
 *
 * A grant holder (E1.5, #1645) is decided by its grant (`decideScreen`) once
 * `/access/me` answers; until then the role decides, exactly as before — a
 * grant holder may see a role-allowed screen for that moment, which shows
 * nothing its grant does not cover: the API answers it by the grant (403 or a
 * projection), never with the role's data. When the answer says `managed`,
 * a screen the grant forbids redirects.
 *
 * Which screen a path belongs to is ONE table (`screenForPath`), and which
 * screens the gate decides is one rule (`isGatedScreen`) — both in
 * `route-permissions.ts`, shared with the Bitrix24 tabs (review #1678).
 *
 * Defense-in-depth only — the backend enforces RBAC via checkPermission() on
 * every API call regardless of what the UI does.
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useRole } from '@/contexts/RoleContext';
import {
  canAccessAdminArea,
  decideScreen,
  isGatedScreen,
  screenForPath,
  waitsForAccess,
  type AccessState,
  type Screen,
} from '@/lib/route-permissions';

const FALLBACK_ROUTE = '/dashboard' as const;

/** The screen the gate decides on this path for this subject, if any. */
function gatedScreenFor(pathname: string, access: AccessState): Screen | undefined {
  const screen = screenForPath(pathname);
  return screen && isGatedScreen(screen, access) ? screen : undefined;
}

export function RouteRoleGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isLoading, globalRole, isOwner, access } = useRole();

  const isAdminArea = isUnderPrefix(pathname, '/admin');
  const screen = gatedScreenFor(pathname, access);
  const decision = screen
    ? decideScreen(screen, { globalRole, isOwner, access })
    : 'allowed';

  // Role still resolving → the boot splash covers the tree. Don't decide on a
  // loading snapshot — that is exactly what bounced legitimate users before.
  const denied =
    !isLoading &&
    ((isAdminArea && !canAccessAdminArea(isOwner)) || decision === 'denied');
  // A screen the role refuses may still be the grant's (a group lead whose
  // base role is `agent` opening voice of customer): while /access/me is in
  // flight, wait for it instead of redirecting — a redirect replaces the deep
  // link, and the answer could not bring it back. Only a screen a grant can
  // OPEN waits (its managed rule is `capabilities`); the admin area, the
  // review queue and role-ruled screens no grant changes redirect at once, as
  // before. Screens the role allows render at once.
  const awaitingAccess =
    denied && !isAdminArea && screen !== undefined && waitsForAccess(screen, access);

  useEffect(() => {
    if (denied && !awaitingAccess) {
      void navigate({ to: FALLBACK_ROUTE, replace: true });
    }
  }, [denied, awaitingAccess, navigate]);

  // While the redirect (or the access answer) is in flight, render nothing so
  // the unauthorized page never flashes.
  if (denied) return null;
  return <>{children}</>;
}

function isUnderPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}
